import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Target, Clock, MapPin, ArrowRight, CalendarClock } from "lucide-react";

const DAYS_SHORT = ["Dim.", "Lun.", "Mar.", "Mer.", "Jeu.", "Ven.", "Sam."];
const MONTHS = ["jan.", "fév.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];

const CATEGORY_GROUPS = [
  { key: "u7", label: "U7", desc: "Baby foot (5-6 ans)", ages: ["U6", "U7"] },
  { key: "u9", label: "U9", desc: "Débutants (7-8 ans)", ages: ["U8", "U9"] },
  { key: "u11", label: "U11", desc: "Poussins (9-10 ans)", ages: ["U10", "U11"] },
  { key: "u13", label: "U13", desc: "Benjamins (11-12 ans)", ages: ["U12", "U13"] },
  { key: "u15", label: "U15", desc: "Minimes (13-14 ans)", ages: ["U14", "U15"] },
  { key: "u17", label: "U17", desc: "Cadets (15-16 ans)", ages: ["U16", "U17"] },
  { key: "foot-loisirs", label: "Foot Loisirs", desc: "Enfants en primaire · mercredi matin", ages: ["LOISIR", "PRIMAIRE"] },
  { key: "seniors", label: "Seniors", desc: "À partir de 19 ans", ages: ["SENIOR"] },
];

function matchesGroup(sessionCategories, group) {
  const text = (sessionCategories || "").toUpperCase();
  if (text.includes("TOUTES") || text.includes("TOUS")) return true;
  return group.ages.some((a) => text.includes(a));
}

function formatDate(d) {
  const dt = new Date(d + "T00:00:00");
  return { day: dt.getDate(), month: MONTHS[dt.getMonth()], weekday: DAYS_SHORT[dt.getDay()] };
}

export default function UpcomingDetections() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/detections?upcoming=true")
      .then((r) => setSessions(r.data))
      .catch(() => setSessions([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;

  return (
    <section className="bg-gradient-to-br from-[#D97706] via-[#B45309] to-[#92400E] text-white" data-testid="detections-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between gap-4 mb-8">
          <div>
            <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-3">
              <Target size={14} /> Séances d'essai
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>
              Prochaines dates de détection
            </h2>
            <p className="mt-2 text-amber-100 max-w-2xl">
              Venez tester un entraînement avec vos coachs. Le dossier d'inscription vous sera remis en main propre à la fin de la séance.
            </p>
          </div>
          <a
            href="#preinscription-form"
            data-testid="detections-cta-preinscr"
            className="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 bg-white text-[#B45309] font-bold rounded-md hover:bg-amber-50 transition-colors text-sm shrink-0"
          >
            Je m'inscris en ligne <ArrowRight size={14} />
          </a>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="detections-grid">
          {CATEGORY_GROUPS.map((group) => {
            const groupSessions = sessions.filter((s) => matchesGroup(s.categories, group));
            return (
              <div
                key={group.key}
                className="rounded-lg bg-white text-[#0F172A] p-5 hover:-translate-y-1 transition-transform shadow-lg flex flex-col"
                data-testid={`detection-group-${group.key}`}
              >
                <div className="flex items-center justify-between mb-3 pb-3 border-b border-[#E2E8F0]">
                  <div>
                    <div className="font-bold text-base" style={{ fontFamily: "Outfit" }}>{group.label}</div>
                    <div className="text-[11px] text-[#475569]">{group.desc}</div>
                  </div>
                  <div className="w-9 h-9 rounded-md bg-[#D97706]/10 flex items-center justify-center text-[#D97706]">
                    <CalendarClock size={16} />
                  </div>
                </div>

                {groupSessions.length === 0 ? (
                  <div className="flex-1 flex flex-col items-center justify-center text-center py-4" data-testid={`detection-${group.key}-empty`}>
                    <div className="text-[11px] uppercase tracking-widest text-[#D97706] font-bold">À venir</div>
                    <div className="mt-1 text-sm text-[#475569] font-semibold">Dates à venir prochainement</div>
                    <div className="mt-1 text-[11px] text-[#475569]">Faites votre pré-inscription — nous vous contacterons dès qu'une date est planifiée.</div>
                  </div>
                ) : (
                  <ul className="flex-1 space-y-2.5" data-testid={`detection-${group.key}-list`}>
                    {groupSessions.map((s) => {
                      const f = formatDate(s.date);
                      return (
                        <li key={s.id} className="flex items-start gap-3 p-2.5 rounded-md bg-slate-50 border border-[#E2E8F0]">
                          <div className="shrink-0 w-12 text-center rounded-md bg-[#0F172A] text-white py-1.5">
                            <div className="text-[9px] uppercase tracking-widest text-amber-300 font-bold leading-tight">{f.month}</div>
                            <div className="text-lg font-black leading-none" style={{ fontFamily: "Outfit" }}>{f.day}</div>
                            <div className="text-[9px] text-slate-300 leading-tight">{f.weekday}</div>
                          </div>
                          <div className="flex-1 min-w-0 text-xs">
                            <div className="flex items-center gap-1 text-[#0F172A] font-semibold">
                              <Clock size={11} /> {s.start_time}{s.end_time ? ` – ${s.end_time}` : ""}
                            </div>
                            <div className="mt-0.5 text-[#475569] flex items-start gap-1 truncate">
                              <MapPin size={11} className="shrink-0 mt-0.5" /> <span className="truncate">{s.location}</span>
                            </div>
                            {s.notes && <div className="mt-1 text-[11px] text-[#475569] italic truncate">{s.notes}</div>}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </div>

        <a
          href="#preinscription-form"
          data-testid="detections-cta-preinscr-mobile"
          className="sm:hidden mt-6 inline-flex items-center gap-2 px-5 py-2.5 bg-white text-[#B45309] font-bold rounded-md transition-colors text-sm"
        >
          Je m'inscris en ligne <ArrowRight size={14} />
        </a>
      </div>
    </section>
  );
}
